"use client";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface DropdownProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
}

export const Dropdown: React.FC<DropdownProps> = ({
  isOpen,
  onClose,
  children,
  className = "",
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [positionClass, setPositionClass] = useState("right-0 mt-2");

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest(".dropdown-toggle")
      ) {
        onClose();
      }
    };

    if (isOpen) {
      // Close any other dropdowns
      document.querySelectorAll("[data-dropdown]").forEach((el) => {
        if (el !== dropdownRef.current) {
          // Trigger close on other dropdowns by emitting event
          el.setAttribute("data-should-close", "true");
        }
      });

      document.addEventListener("mousedown", handleClickOutside);

      // Calculate optimal position based on viewport
      setTimeout(() => {
        if (dropdownRef.current) {
          const rect = dropdownRef.current.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const viewportWidth = window.innerWidth;

          let newPositionClass = "right-0 mt-2";

          // Check if dropdown is cut off at bottom on desktop
          if (rect.bottom > viewportHeight - 10 && rect.top > 200) {
            newPositionClass = "right-0 -mt-2 bottom-full";
          }

          // Check if dropdown is cut off at right (on desktop)
          if (rect.right > viewportWidth - 10) {
            newPositionClass = newPositionClass.replace("right-0", "left-0");
          }

          setPositionClass(newPositionClass);
        }
      }, 0);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={dropdownRef}
          initial={{ opacity: 0, scale: 0.95, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -10 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          data-dropdown="true"
          className={`absolute z-200 ${positionClass} rounded-xl border border-gray-200 bg-white shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark min-w-48 sm:min-w-56 max-w-xs sm:max-w-sm ${className}`}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
